'use client'

import { useMutation } from '@apollo/client/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Meta } from '@/components/atoms/Meta/Meta'
import { getRandomNetworkColor } from '@/components/molecules/Form/Networks/networkColorPalette'
import {
  StepWizardProvider,
  useStepWizard,
} from '@/components/wizard/StepWizardContext'
import {
  NetworkNewWizard_CreateNetworkDocument,
  NetworkNewWizard_RequestCircleJoinDocument,
  NetworkNewWizard_UpdateNetworkDocument,
} from '@/graphql/api/generated'
import { useSwitchContext } from '@/hooks/useSwitchContext'
import { useTracking } from '@/hooks/useTracking'
import {
  NETWORK_MEMBERS_WELCOME_TOUR_ID,
  PRODUCT_TOUR_QUERY_PARAM,
} from '@/productTour/tourIds'
import { serializeProductTourUrl } from '@/productTour/tourSearchParams'
import { NetworksRoutes } from '@/routes/_hooks/routes.enum'
import { generatePath, useNavigate } from '@/routing'
import { bookingSuccessConfettiPreset } from '@/utils/confetti/bookingSuccessConfettiPreset'
import { fireViewportConfettiNearToastStack } from '@/utils/confetti/viewportConfetti'

import { NetworkNewWizardCompositionStep } from './NetworkNewWizardCompositionStep'
import { NetworkNewWizardGeneralStep } from './NetworkNewWizardGeneralStep'
import { NetworkWizardNavProvider } from './NetworkWizardNavContext'
import {
  defaultNetworkWizardValues,
  type NetworkWizardFormValues,
} from './networkWizardTypes'

const STEP_COUNT = 2

/** 0-based index for react-hook-form trigger groups */
const STEP_FIELD_GROUPS: (keyof NetworkWizardFormValues)[][] = [['name'], []]

const WIZARD_STEP_COMPONENTS = [
  NetworkNewWizardGeneralStep,
  NetworkNewWizardCompositionStep,
]

const NetworkNewWizardInner = () => {
  const { currentStep, setTotalSteps } = useStepWizard()

  useEffect(() => {
    setTotalSteps(STEP_COUNT)
  }, [setTotalSteps])

  const ActiveStep = WIZARD_STEP_COMPONENTS[currentStep - 1]
  return ActiveStep ? <ActiveStep /> : null
}

export const NetworkNewWizard = ({
  onCompleted,
}: {
  /** Fired after the network is created (before navigation), e.g. to release the onboarding gate. */
  onCompleted?: () => void
} = {}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { captureEvent } = useTracking()
  const [currentStep, setCurrentStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)

  const methods = useForm<NetworkWizardFormValues>({
    defaultValues: defaultNetworkWizardValues,
  })
  const { trigger, handleSubmit, getValues, setValue } = methods

  // Seed the optional primary color with a random on-brand value. Done in an
  // effect (not in defaultValues) so the server and client renders match.
  useEffect(() => {
    if (!getValues('primaryColor')) {
      setValue('primaryColor', getRandomNetworkColor())
    }
  }, [getValues, setValue])

  const [createNetwork] = useMutation(NetworkNewWizard_CreateNetworkDocument)
  const [updateNetwork] = useMutation(NetworkNewWizard_UpdateNetworkDocument)
  const [requestJoin] = useMutation(NetworkNewWizard_RequestCircleJoinDocument)
  const { switchContext } = useSwitchContext()

  const onFinalSubmit = useCallback(
    async (values: NetworkWizardFormValues) => {
      setSubmitting(true)
      captureEvent('network_wizard_submit_started', {
        has_logo: Boolean(values.logo),
        managed_count: values.managedCircleIris.length,
        external_count: values.externalCircles.length,
      })
      try {
        const createRes = await createNetwork({
          variables: {
            input: {
              name: values.name,
              description: values.description || undefined,
              address: values.address || undefined,
              latitude: values.latitude || undefined,
              longitude: values.longitude || undefined,
              explicitLocationLabel: values.explicitLocationLabel || undefined,
              shareMaterialsWithAllNetworkCommunitiesByDefault:
                values.shareMaterialsWithAllNetworkCommunitiesByDefault,
              logo: values.logo instanceof File ? values.logo : undefined,
              primaryColor: values.primaryColor || undefined,
            },
          },
        })

        const network = createRes.data?.createNetwork?.network
        if (!network?.id || !network.slug) {
          throw new Error('missing network')
        }

        if (values.managedCircleIris.length > 0) {
          await updateNetwork({
            variables: {
              input: {
                id: network.id,
                circles: values.managedCircleIris,
              },
            },
          })
        }

        const externalOnly = values.externalCircles.filter(
          (c) => !values.managedCircleIris.includes(c.id)
        )
        for (const c of externalOnly) {
          await requestJoin({
            variables: {
              input: {
                network: network.id,
                circle: c.id,
              },
            },
          })
        }

        // Switch JWT / app context to this network before opening `/n/...` so RequireAuth does not
        // show the "switch to network context" dialog (same as confirming that dialog manually).
        try {
          await switchContext(network.id, network.name)
        } catch {
          setSubmitting(false)
          return
        }

        fireViewportConfettiNearToastStack(bookingSuccessConfettiPreset)
        toast.success(t('toasts:networks.wizard.success'))
        captureEvent('network_wizard_completed', {
          network_id: network.id,
          network_slug: network.slug,
        })
        onCompleted?.()
        navigate(
          serializeProductTourUrl(
            generatePath(NetworksRoutes.Members, { slug: network.slug }),
            { [PRODUCT_TOUR_QUERY_PARAM]: NETWORK_MEMBERS_WELCOME_TOUR_ID }
          )
        )
      } catch {
        toast.error(t('toasts:networks.wizard.error'))
        captureEvent('network_wizard_error', {})
      } finally {
        setSubmitting(false)
      }
    },
    [
      captureEvent,
      createNetwork,
      navigate,
      onCompleted,
      requestJoin,
      switchContext,
      t,
      updateNetwork,
    ]
  )

  const goNext = useCallback(async () => {
    const stepIndex = currentStep - 1
    const fields = STEP_FIELD_GROUPS[stepIndex] ?? []
    if (fields.length > 0) {
      const ok = await trigger(fields, { shouldFocus: true })
      if (!ok) {
        return
      }
    }
    if (currentStep < STEP_COUNT) {
      setCurrentStep((s) => Math.min(s + 1, STEP_COUNT))
    } else {
      await handleSubmit(onFinalSubmit)()
    }
  }, [currentStep, handleSubmit, onFinalSubmit, trigger])

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(1, s - 1))
  }, [])

  const navValue = useMemo(
    () => ({
      goNext,
      goPrev,
      submitting,
      isFirstStep: currentStep <= 1,
      isLastStep: currentStep >= STEP_COUNT,
    }),
    [currentStep, goNext, goPrev, submitting]
  )

  return (
    <FormProvider {...methods}>
      <StepWizardProvider
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        totalSteps={STEP_COUNT}
      >
        <NetworkWizardNavProvider value={navValue}>
          <NetworkNewWizardInner />
        </NetworkWizardNavProvider>
      </StepWizardProvider>
    </FormProvider>
  )
}

export const NetworkNewWizardView = () => {
  const { t } = useTranslation()
  return (
    <>
      <Meta title={t('networks.wizard.meta.title')} />
      <NetworkNewWizard />
    </>
  )
}
